"use client"

import dynamic from "next/dynamic"
import { useContext } from "react"
import { usePathname } from "next/navigation"
import { ChatContext } from "@/lib/ai-chat/chat-context"
import { useAuthStore } from "@/stores/auth-store"
import { HeaderAskAiButton } from "@/components/ai-chat/header-ask-ai-button"
import { LanguageSwitcher } from "@/components/language-switcher"
import { HeaderControlsBar } from "@/components/header-controls-bar"
// FIX (portal-manager-fix-wave-1, 2026-05-12): the three private copies of
// the hidden-routes list in HeaderControls, FeedbackWidget, and ChatCompanion
// had drifted (/teams-portal + /user-portal were missing from all three).
// Now sourced from a single shared module so adding a route hides chrome
// everywhere in one edit.
import { isHiddenChromeRoute } from "@/lib/routing/hidden-route-prefixes"

const ThemeSwitcher = dynamic(
  () => import("@/components/theme-switcher").then((m) => ({ default: m.ThemeSwitcher })),
  { ssr: false },
)

/**
 * HeaderControls — fixed top-end cluster (top-right, top-left in Arabic)
 * present on every authed page.
 * Order (2026-05-10 round-2): [Language]  [Ask AI]  [Theme]
 *
 * Language sits first (at the cluster's start) so the icon-only globe
 * reads as a separate utility from the two action buttons (Ask AI + Theme)
 * grouped after it.
 *
 * - Auth-gated: hidden on public routes (login, /auth/*, /portal/*).
 * - Route-gated: hidden on /admin, /portal, /player, etc. — even when those
 *   surfaces ARE authenticated (super-admin / portal-user contexts), the
 *   tenant-scoped "Ask AI" button is meaningless there.
 * - Hidden when chat is open (drawer/pip/maximized) — the panel takes over.
 * - Both buttons use the same variant + size to look identical in the chrome.
 */
export function HeaderControls() {
  const ctx = useContext(ChatContext)
  const pathname = usePathname()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  if (isHiddenChromeRoute(pathname)) return null
  if (!isAuthenticated) return null
  if (ctx && ctx.companionMode !== "closed") return null

  return (
    <HeaderControlsBar testId="header-controls">
      <LanguageSwitcher />
      <HeaderAskAiButton />
      <ThemeSwitcher />
    </HeaderControlsBar>
  )
}
